import React, { FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { I18n, Translate } from 'react-redux-i18n';

import { userApi, PaymentApi } from '@api/index';
import Form from '@ui/Form';
import Button from '@ui/Button';
import {
  InfiniteScrollSelectField,
  DatePickerFormField,
  InputNumberFormField,
  SelectFormField,
  CheckBoxFormField,
} from '@ui/Form/Fields';
import { getSchema } from './schema';
import { CURRENCY_OPTIONS, STATUS_OPTIONS } from './constants';
import { ManualPaymentFormProps, FormValues } from './interfaces';

const ManualPaymentForm: FC<ManualPaymentFormProps> = ({
  payment,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const defaultValues = {
    created: payment ? payment.created : '',
    price: payment ? payment.price : 0,
    currency: payment ? payment.currency : 'RUB',
    email: payment ? payment.email : '',
    status: payment ? payment.status : 'COMPLETED',
    isAddLessons: false,
    lessonCount: 0,
  };

  const formMethods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(getSchema()),
  });
  const { watch } = formMethods;
  const selectedUser = watch('email');
  const isAddLessonsValue = watch('isAddLessons');

  const handleOnSubmit = async ({ isAddLessons, ...values }: FormValues) => {
    setIsLoading(true);
    const newPayment = payment
      ? await PaymentApi.updateManualPayment({ id: payment.id, ...values })
      : await PaymentApi.createManualPayment(values);
    setIsLoading(false);

    if (onSubmit) {
      onSubmit(newPayment);
    }
  };

  const studentsMapper = useCallback((data: Array<any>, searchValue) => {
    if (!data.length) {
      return [{ label: searchValue, value: searchValue }];
    }

    return data.map(({ email, lastLessonCourse: course }) => {
      return {
        label: email,
        value: email,
      };
    });
  }, []);

  return (
    <Form formMethods={formMethods} onSubmit={handleOnSubmit}>
      <DatePickerFormField
        name="created"
        label={I18n.t('admin.paymentsStats.paymentDate')}
        disabled={isLoading}
      />
      <InfiniteScrollSelectField
        name="email"
        label={I18n.t('main.user')}
        api={userApi.getStudentsByEmail}
        values={{
          email: selectedUser,
        }}
        searchParamName="email"
        mapper={studentsMapper}
        disabled={isLoading}
      />
      <InputNumberFormField
        name="price"
        label={I18n.t('admin.paymentsStats.sum')}
        disabled={isLoading}
      />
      <SelectFormField
        name="currency"
        label={I18n.t('admin.paymentsStats.currency')}
        options={CURRENCY_OPTIONS}
        disabled={isLoading}
      />
      <SelectFormField
        name="status"
        label={I18n.t('main.status')}
        options={STATUS_OPTIONS}
        disabled={isLoading}
      />
      {!payment && (
        <>
          <CheckBoxFormField name="isAddLessons" disabled={isLoading}>
            Добавить уроки
          </CheckBoxFormField>
          {isAddLessonsValue && (
            <InputNumberFormField
              name="lessonCount"
              label={I18n.t('admin.paymentsStats.lessonsCount')}
              disabled={isLoading}
            />
          )}
        </>
      )}

      <Button type="submit" color="green" disabled={isLoading}>
        {payment ? I18n.t('main.buttons.save') : I18n.t('main.buttons.create')}
      </Button>
    </Form>
  );
};

export default ManualPaymentForm;
